// src/components/store/sections/deal/Variant3FlashGrid.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Clock, Flame, ArrowRight } from "lucide-react";
import { useProductTimer } from "./useProductTimer";

interface Props {
  products: any[];
  subdomain: string;
}

export default function Variant3FlashGrid({ products, subdomain }: Props) {
  const resolveImageUrl = (raw?: string | null) => {
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) {
      if (raw.includes("127.0.0.1:8000")) {
        return raw.replace("127.0.0.1:8000", `${subdomain}.localhost:8000`);
      }
      return raw;
    }
    const clean = raw.startsWith("/") ? raw : `/${raw}`;
    return `http://${subdomain}.localhost:8000${clean}`;
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 font-sans select-none space-y-6">
      <div className="flex items-center justify-between gap-4 border-b pb-4">
        <div>
          <span className="text-xs font-black text-rose-600 uppercase tracking-wider flex items-center gap-1">
            <Flame className="w-4 h-4 fill-rose-600" /> Flash Deals Catalog
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">
            Deals of the Day ({products.length})
          </h2>
        </div>
        <Link
          href="/products"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs"
        >
          <span>All Deals</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {products.map((p: any) => (
          <FlashCard key={p.id} product={p} resolveImageUrl={resolveImageUrl} />
        ))}
      </div>
    </section>
  );
}

function FlashCard({ product, resolveImageUrl }: { product: any; resolveImageUrl: any }) {
  const timer = useProductTimer(product);
  const currentPrice = Number(product?.price || 0);
  const oldPrice = product?.compare_price ? Number(product.compare_price) : null;
  const discountPercent =
    oldPrice && oldPrice > currentPrice
      ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
      : 20;

  const imgUrl = resolveImageUrl(product?.primary_image || product?.image);
  const slug = product?.slug || product?.id || "";

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-4 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
      <div>
        <div className="relative w-full h-44 rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center p-2 mb-3">
          <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-black z-10">
            {discountPercent}% OFF
          </span>
          {imgUrl ? (
            <Image
              src={imgUrl}
              alt={product?.name || "Deal"}
              fill
              unoptimized
              className="object-contain"
            />
          ) : (
            <div className="text-slate-300 text-xs font-bold">No Image</div>
          )}
        </div>

        <Link
          href={`/products/${slug}`}
          className="text-sm font-black text-slate-800 hover:text-blue-600 line-clamp-1 block"
        >
          {product?.name || "Deal Item"}
        </Link>

        <div className="flex items-center gap-1 text-[11px] font-bold text-amber-500 mt-1">
          <Star className="w-3 h-3 fill-amber-400" />
          <span>5.0</span>
        </div>

        <div className="flex items-baseline gap-2 mt-2 font-mono">
          <span className="text-lg font-black text-slate-900">৳{currentPrice.toLocaleString()}</span>
          {oldPrice && <span className="text-xs text-slate-400 line-through">৳{oldPrice.toLocaleString()}</span>}
        </div>
      </div>

      <div className="space-y-2 mt-3 pt-2.5 border-t border-slate-100">
        <div className="text-[10px] font-mono text-slate-600 flex items-center justify-between font-bold">
          <span className="text-rose-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Ends:
          </span>
          <span>
            {String(timer.hours).padStart(2, "0")}h:{String(timer.minutes).padStart(2, "0")}m:
            <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
          </span>
        </div>

        <Link
          href={`/products/${slug}`}
          className="w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs flex items-center justify-center gap-1"
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          <span>Shop Now</span>
        </Link>
      </div>
    </div>
  );
}